import { useCallback, useMemo, useState } from "react";
import { TWebsiteBuilderCopyableNode } from "../../nodes/websiteBuilderNode";
import { cloneDeep } from "lodash";

type TWebsiteBuilderClipboardCopy = (copyableNode: TWebsiteBuilderCopyableNode) => void;
type TWebsiteBuilderClipboardPeek = () => TWebsiteBuilderCopyableNode;

export interface IWebsiteBuilderClipboard {
  hasElement: boolean;
  empty: boolean;
  copy: TWebsiteBuilderClipboardCopy;
  peek: TWebsiteBuilderClipboardPeek;
}

export function useWebsiteBuilderClipboard(): IWebsiteBuilderClipboard {
  const [copiedNode, setCopiedNode] = useState<TWebsiteBuilderCopyableNode>(null);

  const hasElement: boolean = useMemo(() => {
    return copiedNode?.type === "element";
  }, [copiedNode?.type]);

  const empty: boolean = useMemo(() => {
    return copiedNode == null;
  }, [copiedNode]);

  const copy: TWebsiteBuilderClipboardCopy = useCallback(copyableNode => {
    setCopiedNode(cloneDeep(copyableNode));
  }, []);

  const peek: TWebsiteBuilderClipboardPeek = useCallback(() => {
    if (copiedNode == null) {
      return null;
    } else if (copiedNode.type === "element") {
      return {
        ...copiedNode,
        id: crypto.randomUUID(),
      };
    } else if (copiedNode.type === "section") {
      return {
        ...copiedNode,
        id: crypto.randomUUID(),
        children: copiedNode.children.map(sectionChild => {
          if (sectionChild.type === "element") {
            return {
              ...sectionChild,
              id: crypto.randomUUID(),
            };
          } else {
            return {
              ...sectionChild,
              id: crypto.randomUUID(),
              children: sectionChild.children.map(containerChild => ({
                ...containerChild,
                id: crypto.randomUUID(),
              })),
            };
          }
        }),
      };
    } else {
      return {
        ...copiedNode,
        id: crypto.randomUUID(),
        children: copiedNode.children.map(child => ({
          ...child,
          id: crypto.randomUUID(),
        })),
      };
    }
  }, [copiedNode]);

  const clipboard: IWebsiteBuilderClipboard = useMemo(() => {
    return {
      hasElement,
      empty,
      copy,
      peek,
    };
  }, [hasElement, empty, copy, peek]);

  return clipboard;
}
